import { useCallback, useEffect, useState } from "react";

const KEY = "ssm-favorites";
const listeners = new Set<() => void>();

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

let cache: string[] = readFavorites();

function writeFavorites(next: string[]) {
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
  listeners.forEach((listener) => listener());
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(cache);

  useEffect(() => {
    const listener = () => setFavorites([...cache]);
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const toggleFavorite = useCallback((id: string) => {
    const next = cache.includes(id)
      ? cache.filter((item) => item !== id)
      : [...cache, id];
    writeFavorites(next);
  }, []);

  const isFavorite = useCallback(
    (id: string) => favorites.includes(id),
    [favorites]
  );

  return { favorites, toggleFavorite, isFavorite, count: favorites.length };
}